import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { Variant } from '@components/ui/Toast';
import { AuthContextValue } from '@auth/types/AuthTypes';

export class HttpClient {
  private client: AxiosInstance;
  private showToast: (message: string, variant: Variant) => void;
  private getAuthContext: () => AuthContextValue;
  private isToastDisplayed = false;

  constructor(
    baseURL: string,
    showToast: (message: string, variant: Variant) => void,
    getAuthContext: () => AuthContextValue
  ) {
    // Prefer runtime override via window.apiEndpoint, then explicit baseURL,
    // then fallback to the historical default used in Admin.Web for local dev.
    const resolvedBaseURL = window.apiEndpoint || baseURL || 'http://localhost:8080';

    this.client = axios.create({
      baseURL: resolvedBaseURL,
      timeout: 30000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    this.showToast = showToast;
    this.getAuthContext = getAuthContext;

    this.setupInterceptors();
  }

  private async addTokenToHeaders(
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    const authContext = this.getAuthContext();

    if (authContext.token) {
      config.headers.Authorization = `Bearer ${authContext.token}`;
    }

    if (authContext.mode === 'entra') {
      const orgToken = sessionStorage.getItem('organizationToken');
      if (orgToken) {
        config.headers['organizationToken'] = orgToken;
      }
    }

    return config;
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      async (
        config: InternalAxiosRequestConfig
      ): Promise<InternalAxiosRequestConfig> => {
        const updatedConfig = await this.addTokenToHeaders(config);
        return updatedConfig;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest?._retry) {
          originalRequest._retry = true;

          try {
            const authContext = this.getAuthContext();
            const newToken = await authContext.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            // AuthProvider will show TokenRefreshError UI
            console.error('Token refresh failed in HttpClient:', refreshError);
            return Promise.reject(refreshError);
          }
        }

        if (
          (error.response?.status === 500 || error.response?.status === 404) &&
          !this.isToastDisplayed
        ) {
          this.isToastDisplayed = true;
          this.showToast(
            'Something went wrong, and we can’t show you the content you asked for right now. Please try again later.',
            Variant.Error
          );
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: unknown): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(
    url: string,
    data: unknown,
    options?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, options);
    return response.data;
  }

  async put<T>(url: string, data: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async patch<T>(url: string, data: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }
}
